import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { InputFieldProps, Overlay } from "shared/components";
import { ISelectOption } from "shared/components/Form/Fields/select/types";
import { FieldWrapper } from "../FieldWrapper";
import { InputFieldWrapper, StyledInputField } from "../styles";

import { OptionProps } from "react-select";
import { DefaultOption } from "../select/components";
import { config } from "../select/config";
import { StyledDropdown, StyledReactSelect } from "../select/styles";
import { BoldText } from "shared/styles";

export interface PlacesInputProps extends InputFieldProps {
  options: ISelectOption[];
  value?: string;
  onDropdownClose?: () => void;
  isLoading?: boolean;
  defaultValue?: string;
  onOptionSelect?: (option: ISelectOption) => void;
}

export const PlacesInput: React.FC<PlacesInputProps> = ({
  value,
  name,
  label,
  style,
  options = [],
  onChange,
  onOptionSelect,
  onDropdownClose,
  isLoading = false,
  defaultValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<any>(null); // eslint-disable-line @typescript-eslint/no-explicit-any

  const dropdownOptions = useMemo(() => {
    return [
      {
        value: value,
        label: value,
        isLabel: false,
        isDisabled: false,
        alwaysShow: true,
        labelComponent: <BoldText>{value}</BoldText>,
      },
      ...options.filter(option => !option.isLabel),
    ];
  }, [options, value]);

  const navigableOptions = [...dropdownOptions.filter(option => !option.isLabel)];

  // Custom Option component that uses our focus state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomOption: React.FC<OptionProps<any>> = props => {
    const { data, ...rest } = props;
    const optionIndex = dropdownOptions.findIndex(option => option.value === data.value);
    const isFocused = optionIndex === focusedOptionIndex;

    return (
      <div data-option-index={optionIndex}>
        <DefaultOption {...rest} data={data} isFocused={isFocused} />
      </div>
    );
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const shouldOpen = newValue.length > 0;

    onChange?.(newValue);
    setIsOpen(shouldOpen);

    if (shouldOpen) {
      setFocusedOptionIndex(-1); // Reset focus when opening dropdown
    }
  };

  const handleOptionSelect = (option: ISelectOption) => {
    onOptionSelect?.(option);
    onChange?.(option.label);

    setIsOpen(false);

    if (onDropdownClose) {
      onDropdownClose();
    }
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setFocusedOptionIndex(-1);
    if (onDropdownClose) {
      onDropdownClose();
    }
  }, [onDropdownClose]);

  const handleFocus = () => {
    if (value) {
      setIsOpen(true);
      setFocusedOptionIndex(-1); // Reset focus when opening dropdown
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || navigableOptions.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setFocusedOptionIndex(prevIndex => {
          // Find current navigable option index
          const currentNavigableIndex =
            prevIndex >= 0
              ? navigableOptions.findIndex(option => dropdownOptions.indexOf(option) === prevIndex)
              : -1;

          // Get next navigable option
          const nextNavigableIndex =
            currentNavigableIndex < navigableOptions.length - 1 ? currentNavigableIndex + 1 : 0;

          // Return the actual index in the full options array
          return dropdownOptions.indexOf(navigableOptions[nextNavigableIndex]);
        });
        break;
      case "ArrowUp":
        event.preventDefault();
        setFocusedOptionIndex(prevIndex => {
          // Find current navigable option index
          const currentNavigableIndex =
            prevIndex >= 0
              ? navigableOptions.findIndex(option => dropdownOptions.indexOf(option) === prevIndex)
              : -1;

          // Get previous navigable option
          const prevNavigableIndex =
            currentNavigableIndex > 0 ? currentNavigableIndex - 1 : navigableOptions.length - 1;

          // Return the actual index in the full options array
          return dropdownOptions.indexOf(navigableOptions[prevNavigableIndex]);
        });
        break;
      case "Enter":
        event.preventDefault();
        if (focusedOptionIndex >= 0 && focusedOptionIndex < dropdownOptions.length) {
          const selectedOption = dropdownOptions[focusedOptionIndex];
          if (!selectedOption.isLabel && !selectedOption.isDisabled) {
            handleOptionSelect(selectedOption as ISelectOption);
          }
        }
        break;
      case "Escape":
        event.preventDefault();
        setFocusedOptionIndex(-1);
        handleClose();
        break;
      default:
        break;
    }
  };

  // Scroll focused option into view within dropdown only
  useEffect(() => {
    if (isOpen && focusedOptionIndex >= 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const menuList = dropdownRef.current?.querySelector(".select__menu-list") as HTMLElement;
        const focusedOption = dropdownRef.current?.querySelector(
          `[data-option-index="${focusedOptionIndex}"]`
        ) as HTMLElement;

        if (menuList && focusedOption) {
          const menuListScrollTop = menuList.scrollTop;
          const menuListHeight = menuList.clientHeight;
          const optionOffsetTop = focusedOption.offsetTop;
          const optionHeight = focusedOption.offsetHeight;

          // Check if option is above visible area
          if (optionOffsetTop < menuListScrollTop) {
            menuList.scrollTo({
              top: optionOffsetTop,
              behavior: "smooth",
            });
          }
          // Check if option is below visible area
          else if (optionOffsetTop + optionHeight > menuListScrollTop + menuListHeight) {
            menuList.scrollTo({
              top: optionOffsetTop + optionHeight - menuListHeight,
              behavior: "smooth",
            });
          }
        }
      }, 0);
    }
  }, [focusedOptionIndex, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, handleClose]);

  return (
    <FieldWrapper name={name} label={label} style={style}>
      <div style={{ position: "relative" }}>
        <InputFieldWrapper isFocused={isOpen}>
          <StyledInputField
            ref={inputRef}
            type="text"
            name={name}
            id={name}
            value={value}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            defaultValue={defaultValue}
          />
        </InputFieldWrapper>

        <StyledDropdown ref={dropdownRef}>
          {isOpen && <Overlay onClick={handleClose} />}

          {isOpen && dropdownOptions.length > 0 && (
            <StyledReactSelect
              ref={selectRef}
              {...config}
              autoFocus={false}
              menuIsOpen={true}
              value={null}
              inputValue=""
              placeholder=""
              options={dropdownOptions}
              isLoading={isLoading}
              onChange={(newValue: unknown) => {
                const option = newValue as ISelectOption;
                if (option && option.onClick) {
                  option.onClick();
                } else {
                  handleOptionSelect(option);
                }
              }}
              onBlur={() => {
                setTimeout(() => {
                  handleClose();
                }, 150);
              }}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  inputRef.current?.focus();
                  handleClose();
                }
              }}
              components={{
                Control: () => null,
                DropdownIndicator: null,
                IndicatorSeparator: null,
                Option: CustomOption,
              }}
              styles={{
                menu: (provided: Record<string, unknown>) => ({
                  ...provided,
                  position: "static",
                  boxShadow: "none",
                  border: "none",
                }),
                menuList: (provided: Record<string, unknown>) => ({
                  ...provided,
                  maxHeight: "200px",
                }),
              }}
            />
          )}
        </StyledDropdown>
      </div>
    </FieldWrapper>
  );
};
